import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import { InlineToolbarButtons, Plugins, PluginToolbarButtons } from '../DaEditor/enums';
import { EditorFlavours } from './types';
import { Image } from './PluginComponents/Image';
import { Gallery } from './PluginComponents/Gallery';
import { getAnchorBlockPreview } from './PluginComponents/AnchorBlockPreview';
import { MediaSourceType } from '../MediaPopup/types';
import { getDeviationType } from '../DeviationViews/_helpers';
import { DeviationTypes } from '../types/deviation';
import { isEmptyFeature, wordCountFeature, mediaCountFeature, firstImageFeature } from './Features';
import { ToolbarType } from 'wix-rich-content-common';
import { Gif, Link, AddImage, CenterImage, FullWidthImage, FloatLeft, FloatRight, Edit, Delete, AddVideo, AddGallery, GalleryTorpedo, GalleryGrid, Settings } from '../DaEditor/components/icons';
import { colorScheme, customBackgroundStyleFn, customForegroundStyleFn, styleSelectionPredicate } from '../DaEditor/plugins/text-color-style-fn';
import PortalDecorator from '../DaEditor/components/PortalDecorator';
import { fetchUrlPreviewData } from '../DaEditor/plugins/plugin-link-preview/util';
import EmojiIcon from '@wix/da-ds/pkg/Icons/24x24/AddEmoticon';
const imageCanBeLinked = (componentData, currentUser)=>{
    if (componentData && componentData.data) {
        var _componentData_data_author;
        // can be linked only if not published or the user owns it
        return !componentData.data.isPublished || ((_componentData_data_author = componentData.data.author) === null || _componentData_data_author === void 0 ? void 0 : _componentData_data_author.userId) === (currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId);
    }
    return true;
};
const imageCanBeFullWidth = (componentData)=>{
    if (componentData && componentData.data && componentData.data.deviationId) {
        return getDeviationType(componentData.data) === DeviationTypes.IMAGE;
    }
    return true;
};
const giphyConfig = {
    toolbar: {
        icons: {
            InsertPluginButtonIcon: Gif,
            sizeContent: CenterImage,
            sizeFullWidth: FullWidthImage,
            sizeSmallLeft: FloatLeft,
            sizeSimallRight: FloatRight,
            link: Link,
            replace: Edit,
            delete: Delete
        },
        hidden: [
            'sizeOriginal',
            'sizeFullWidth',
            'sizeContent',
            'separator1',
            'separator2'
        ]
    },
    giphySdkApiKey: 'dCf5MVSF00OQO1mlKr5DoNPO9OpOrlhj',
    componentDataDefaults: {
        config: {
            size: 'small',
            alignment: 'center'
        }
    },
    insertToolbars: [
        ToolbarType.SIDE,
        ToolbarType.FOOTER,
        ToolbarType.INSERT_PLUGIN
    ]
};
const mentionConfig = {
    reposition: true,
    entryHeight: 40,
    additionalHeight: 0
};
const linkConfig = {
    toolbar: {
        icons: {
            InsertPluginButtonIcon: Link
        },
        hidden: []
    }
};
const linkConfigWithAnchors = {
    toolbar: {
        icons: {
            InsertPluginButtonIcon: Link
        },
        hidden: []
    },
    linkTypes: {
        anchor: {
            blockPreview: getAnchorBlockPreview
        }
    }
};
const linkPreviewConfig = {
    enableEmbed: true,
    fetchData: fetchUrlPreviewData,
    config: {
        size: 'content',
        alignment: 'center'
    },
    toolbar: {
        hidden: [
            'link'
        ]
    }
};
const getImagePluginConfig = (toolbarButtons = [
    PluginToolbarButtons.BEST_FIT,
    PluginToolbarButtons.ALIGN_LEFT,
    PluginToolbarButtons.ALIGN_RIGHT,
    PluginToolbarButtons.SEPARATOR,
    PluginToolbarButtons.LINK,
    PluginToolbarButtons.SEPARATOR,
    PluginToolbarButtons.REPLACE,
    PluginToolbarButtons.DELETE,
    PluginToolbarButtons.REFRESH
], allowResize = true)=>({
        component: Image,
        canBeLinked: imageCanBeLinked,
        canBeFullWidth: imageCanBeFullWidth,
        allowResize,
        toolbarButtons,
        toolbar: {
            hidden: [],
            icons: {
                InsertPluginButtonIcon: AddImage,
                sizeContent: CenterImage,
                sizeFullWidth: FullWidthImage,
                sizeSmallLeft: FloatLeft,
                sizeSimallRight: FloatRight,
                link: Link,
                replace: Edit,
                delete: Delete
            }
        }
    });
const getVideoPluginConfig = (toolbarButtons = [
    PluginToolbarButtons.BEST_FIT,
    PluginToolbarButtons.ALIGN_LEFT,
    PluginToolbarButtons.ALIGN_RIGHT,
    PluginToolbarButtons.SEPARATOR,
    PluginToolbarButtons.REPLACE,
    PluginToolbarButtons.DELETE
])=>({
        toolbarButtons,
        toolbar: {
            hidden: [],
            icons: {
                InsertPluginButtonIcon: AddGallery,
                sizeContent: CenterImage,
                sizeFullWidth: FullWidthImage,
                sizeSmallLeft: FloatLeft,
                sizeSimallRight: FloatRight,
                add: Edit,
                delete: Delete
            }
        }
    });
const getGalleryPluginConfig = (toolbarButtons = [
    PluginToolbarButtons.BEST_FIT,
    PluginToolbarButtons.LAYOUT_GRID,
    PluginToolbarButtons.LAYOUT_MASONRY,
    PluginToolbarButtons.SEPARATOR,
    PluginToolbarButtons.REPLACE,
    PluginToolbarButtons.SETTINGS,
    PluginToolbarButtons.DELETE,
    PluginToolbarButtons.REFRESH
])=>({
        component: Gallery,
        toolbarButtons,
        toolbar: {
            hidden: [],
            icons: {
                InsertPluginButtonIcon: AddVideo,
                sizeContent: CenterImage,
                sizeFullWidth: FullWidthImage,
                sizeSmallLeft: FloatLeft,
                sizeSimallRight: FloatRight,
                layoutGrid: GalleryGrid,
                layoutMasonry: GalleryTorpedo,
                advanced_settings: Settings,
                replace: Edit,
                delete: Delete
            }
        }
    });
const emojiConfig = {
    toolbar: {
        icons: {
            InsertPluginButtonIcon: EmojiIcon
        }
    }
};
const defaultFeatures = [
    isEmptyFeature,
    wordCountFeature,
    mediaCountFeature,
    firstImageFeature
];
/**
 * Actual config
 *
 * taken from: https://docs.google.com/spreadsheets/d/1yLI5z0DtUlnLpMZlHqUFuynmOTpl1fkryQ4kg-CTUgI/edit?ts=5b291b5a#gid=0
 */ // eslint-disable-next-line @typescript-eslint/no-redeclare
export const EditorFlavourConfigs = {
    [EditorFlavours.Journal]: {
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.ALIGNMENT,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.TITLE,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST,
            InlineToolbarButtons.BLOCKQUOTE
        ],
        featureConfig: [
            isEmptyFeature,
            wordCountFeature,
            mediaCountFeature
        ],
        pluginsConfig: {
            [Plugins.IMAGE]: getImagePluginConfig(),
            [Plugins.GALLERY]: getGalleryPluginConfig(),
            [Plugins.VIDEO]: getVideoPluginConfig(),
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.MENTION]: mentionConfig,
            [Plugins.LINK]: linkConfigWithAnchors,
            [Plugins.LINK_PREVIEW]: linkPreviewConfig,
            [Plugins.DIVIDER]: {},
            [Plugins.TEXT_COLOR]: {},
            [Plugins.GIPHY]: giphyConfig,
            [Plugins.HASHTAG]: {},
            [Plugins.SCROLLER]: {},
            [Plugins.DND_UPLOAD]: {}
        }
    },
    [EditorFlavours.Literature]: {
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.TITLE,
            InlineToolbarButtons.ALIGNMENT,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST,
            InlineToolbarButtons.BLOCKQUOTE
        ],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.IMAGE]: getImagePluginConfig(),
            [Plugins.GALLERY]: getGalleryPluginConfig(),
            [Plugins.VIDEO]: getVideoPluginConfig(),
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.MENTION]: mentionConfig,
            [Plugins.LINK]: linkConfigWithAnchors,
            [Plugins.LINK_PREVIEW]: linkPreviewConfig,
            [Plugins.DIVIDER]: {},
            [Plugins.HASHTAG]: {},
            [Plugins.SCROLLER]: {},
            [Plugins.DND_UPLOAD]: {}
        }
    },
    [EditorFlavours.Status]: {
        extraProps: {
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST
        ],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.IMAGE]: getImagePluginConfig(),
            [Plugins.GALLERY]: getGalleryPluginConfig(),
            [Plugins.VIDEO]: getVideoPluginConfig([
                PluginToolbarButtons.BEST_FIT,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.MENTION]: mentionConfig,
            [Plugins.LINK]: linkConfigWithAnchors,
            [Plugins.LINK_PREVIEW]: linkPreviewConfig,
            [Plugins.DIVIDER]: {},
            [Plugins.GIPHY]: giphyConfig,
            [Plugins.HASHTAG]: {},
            [Plugins.DND_UPLOAD]: {}
        }
    },
    [EditorFlavours.Comment]: {
        extraProps: {
            showMediaAlwaysSmall: true,
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST
        ],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.IMAGE]: getImagePluginConfig([
                PluginToolbarButtons.LINK,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ], false),
            [Plugins.GALLERY]: getGalleryPluginConfig([
                PluginToolbarButtons.LAYOUT_GRID,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.VIDEO]: getVideoPluginConfig([
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.MENTION]: mentionConfig,
            [Plugins.LINK]: linkConfig,
            [Plugins.LINK_PREVIEW]: linkPreviewConfig,
            [Plugins.DIVIDER]: {},
            [Plugins.GIPHY]: _object_spread_props(_object_spread({}, giphyConfig), {
                toolbar: {
                    icons: {
                        InsertPluginButtonIcon: Gif
                    },
                    hidden: [
                        'sizeOriginal',
                        'sizeFullWidth',
                        'sizeSmallLeft',
                        'sizeSimallRight',
                        'sizeSmallCenter',
                        'sizeContent',
                        'separator1',
                        'separator2'
                    ]
                }
            }),
            [Plugins.HASHTAG]: {},
            [Plugins.DND_UPLOAD]: {}
        }
    },
    [EditorFlavours.Commission]: {
        extraProps: {
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST
        ],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.IMAGE]: getImagePluginConfig([
                PluginToolbarButtons.BEST_FIT,
                PluginToolbarButtons.ALIGN_LEFT,
                PluginToolbarButtons.ALIGN_RIGHT,
                PluginToolbarButtons.LINK,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.GALLERY]: getGalleryPluginConfig(),
            [Plugins.VIDEO]: getVideoPluginConfig([
                PluginToolbarButtons.BEST_FIT,
                PluginToolbarButtons.ALIGN_LEFT,
                PluginToolbarButtons.ALIGN_RIGHT,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.LINK]: linkConfig,
            [Plugins.LINK_PREVIEW]: linkPreviewConfig,
            [Plugins.DIVIDER]: {},
            [Plugins.DND_UPLOAD]: {}
        },
        mediaSourceTypes: [
            MediaSourceType.GALLERY,
            MediaSourceType.STASH
        ]
    },
    [EditorFlavours.DeviantId]: {
        extraProps: {
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST
        ],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.IMAGE]: getImagePluginConfig(),
            [Plugins.GALLERY]: getGalleryPluginConfig(),
            [Plugins.VIDEO]: getVideoPluginConfig([
                PluginToolbarButtons.BEST_FIT,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.LINK]: linkConfig,
            [Plugins.MENTION]: mentionConfig,
            [Plugins.DND_UPLOAD]: {}
        }
    },
    [EditorFlavours.Deviation]: {
        extraProps: {
            showMediaAlwaysSmall: true,
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST
        ],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.IMAGE]: getImagePluginConfig([
                PluginToolbarButtons.LINK,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ], false),
            [Plugins.GALLERY]: getGalleryPluginConfig([
                PluginToolbarButtons.LAYOUT_GRID,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.VIDEO]: getVideoPluginConfig([
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.MENTION]: mentionConfig,
            [Plugins.LINK]: linkConfig,
            [Plugins.LINK_PREVIEW]: linkPreviewConfig,
            [Plugins.DIVIDER]: {},
            [Plugins.GIPHY]: _object_spread_props(_object_spread({}, giphyConfig), {
                toolbar: {
                    icons: {
                        InsertPluginButtonIcon: Gif
                    },
                    hidden: [
                        'sizeOriginal',
                        'sizeFullWidth',
                        'sizeSmallLeft',
                        'sizeSimallRight',
                        'sizeSmallCenter',
                        'sizeContent',
                        'separator1',
                        'separator2'
                    ]
                }
            }),
            [Plugins.HASHTAG]: {},
            [Plugins.DND_UPLOAD]: {}
        }
    },
    [EditorFlavours.PollAnswer]: {
        extraProps: {
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {}
        }
    },
    [EditorFlavours.Donation]: {
        extraProps: {
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.LINK
        ],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.LINK]: linkConfig
        }
    },
    [EditorFlavours.CustomBox]: {
        extraProps: {
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE,
            InlineToolbarButtons.TEXT_COLOR,
            InlineToolbarButtons.TEXT_HIGHLIGHT,
            InlineToolbarButtons.ALIGNMENT,
            InlineToolbarButtons.LINK,
            InlineToolbarButtons.SEPARATOR,
            InlineToolbarButtons.TITLE,
            InlineToolbarButtons.ORDERED_LIST,
            InlineToolbarButtons.UNORDERED_LIST,
            InlineToolbarButtons.BLOCKQUOTE
        ],
        textToolbarDecoratorFn: PortalDecorator({
            portalId: 'custombox-text-tools'
        }),
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.IMAGE]: getImagePluginConfig(),
            [Plugins.GALLERY]: getGalleryPluginConfig(),
            [Plugins.VIDEO]: getVideoPluginConfig([
                PluginToolbarButtons.BEST_FIT,
                PluginToolbarButtons.ALIGN_LEFT,
                PluginToolbarButtons.ALIGN_RIGHT,
                PluginToolbarButtons.SEPARATOR,
                PluginToolbarButtons.REPLACE,
                PluginToolbarButtons.DELETE
            ]),
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.MENTION]: mentionConfig,
            [Plugins.LINK]: linkConfigWithAnchors,
            [Plugins.LINK_PREVIEW]: linkPreviewConfig,
            [Plugins.DIVIDER]: {},
            [Plugins.HIGHLIGHT_COLOR]: {
                colorScheme,
                styleSelectionPredicate,
                customStyleFn: customBackgroundStyleFn,
                onColorAdded: (color)=>null,
                getUserColors: ()=>[],
                positionPicker: (buttonRef, panelWidth)=>{
                    const editorWrapper = document.querySelector('[data-editor-key=customText]');
                    if (!editorWrapper || !buttonRef.current) {
                        return {
                            left: 0,
                            top: 0
                        };
                    }
                    const dialog = editorWrapper.closest('[role=dialog]');
                    if (!dialog) {
                        return {
                            left: 0,
                            top: 0
                        };
                    }
                    const modalRect = dialog.getBoundingClientRect();
                    const { top: topButton , left: leftButton , height: heightButton  } = buttonRef.current.getBoundingClientRect();
                    const { top: topModal , left: leftModal  } = modalRect;
                    const panelLeft = leftButton - leftModal - panelWidth / 2;
                    return {
                        left: Math.max(panelLeft, 2),
                        top: Math.max(topButton - topModal + heightButton, 50)
                    };
                }
            },
            [Plugins.TEXT_COLOR]: {
                colorScheme,
                styleSelectionPredicate,
                customStyleFn: customForegroundStyleFn,
                onColorAdded: (color)=>null,
                getUserColors: ()=>[],
                positionPicker: (buttonRef, panelWidth)=>{
                    const editorWrapper = document.querySelector('[data-editor-key=customText]');
                    if (!editorWrapper || !buttonRef.current) {
                        return {
                            left: 0,
                            top: 0
                        };
                    }
                    const dialog = editorWrapper.closest('[role=dialog]');
                    if (!dialog) {
                        return {
                            left: 0,
                            top: 0
                        };
                    }
                    const modalRect = dialog.getBoundingClientRect();
                    const { top: topButton , left: leftButton , height: heightButton  } = buttonRef.current.getBoundingClientRect();
                    const { top: topModal , left: leftModal  } = modalRect;
                    const panelLeft = leftButton - leftModal - panelWidth / 2;
                    return {
                        left: Math.max(panelLeft, 2),
                        top: Math.max(topButton - topModal + heightButton, 50)
                    };
                }
            },
            [Plugins.GIPHY]: giphyConfig,
            [Plugins.HASHTAG]: {},
            [Plugins.INDENT]: {},
            [Plugins.DND_UPLOAD]: {
                pasteOnly: true
            }
        }
    },
    [EditorFlavours.Chat]: {
        extraProps: {
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.BOLD,
            InlineToolbarButtons.ITALIC,
            InlineToolbarButtons.UNDERLINE
        ],
        textToolbarDecoratorFn: PortalDecorator({
            portalId: 'chat-text-tools'
        }),
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.LINK]: linkConfig,
            [Plugins.EMOTES]: {},
            [Plugins.MENTION]: mentionConfig
        }
    },
    [EditorFlavours.GroupAbout]: {
        extraProps: {
            hideSideToolbar: true
        },
        toolbars: [],
        textTools: [
            InlineToolbarButtons.LINK
        ],
        featureConfig: defaultFeatures,
        pluginsConfig: {
            [Plugins.EMOJI]: emojiConfig,
            [Plugins.EMOTES]: {},
            [Plugins.LINK]: linkConfig
        }
    }
};
